// src/pages/Staff.js
import React, { useState, useEffect } from 'react';
import { T, Card, Btn, SectionTitle, Modal, INP, LBL, Spinner, Empty } from '../components/UI';
import { useAuth } from '../context/AuthContext';
import {
  listUsers, listRoles, listPermissions, createRole, updateRole, deleteRole,
  createUser, setUserRole, setUserStatus,
} from '../services/api';

export default function Staff() {
  const { can, user: me } = useAuth();
  const [tab, setTab] = useState('staff');

  if (!can('manage_users') && !can('manage_roles')) {
    return <Empty msg="You don't have access to staff management." />;
  }

  return (
    <div>
      <SectionTitle sub="Manage staff accounts, roles, and permissions">Staff & Roles</SectionTitle>

      <div style={{ display:'flex', gap:'8px', marginBottom:'20px' }}>
        <Btn variant={tab==='staff' ? 'gold' : 'ghost'} onClick={() => setTab('staff')}>Staff Accounts</Btn>
        {can('manage_roles') && <Btn variant={tab==='roles' ? 'gold' : 'ghost'} onClick={() => setTab('roles')}>Roles & Permissions</Btn>}
      </div>

      {tab === 'staff' && <StaffTab me={me} />}
      {tab === 'roles' && can('manage_roles') && <RolesTab />}
    </div>
  );
}

// ───────────────────────────── STAFF TAB ─────────────────────────────
function StaffTab({ me }) {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([listUsers(), listRoles()])
      .then(([u, r]) => { setUsers(u.data.data); setRoles(r.data.data); })
      .catch(() => setMsg({ type: 'error', text: 'Failed to load staff list.' }))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleRoleChange = async (userId, role) => {
    try {
      await setUserRole(userId, role);
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to change role.' });
    }
  };

  const toggleStatus = async (u) => {
    try {
      await setUserStatus(u.id, !u.is_active);
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to update status.' });
    }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      {msg && (
        <div style={{ fontSize:'13px', marginBottom:'14px', color: msg.type==='success' ? T.success : T.danger }}>
          {msg.text}
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'14px' }}>
        <Btn onClick={() => setShowAdd(true)}>+ Add staff member</Btn>
      </div>

      <Card style={{ padding: 0, overflowX: 'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Name</th>
              <th style={th}>Email</th>
              <th style={th}>Role</th>
              <th style={th}>Status</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td style={td}><b>{u.name}</b>{u.id === me?.id && <span style={{ color:T.muted, fontWeight:400 }}> (you)</span>}</td>
                <td style={td}>{u.email}</td>
                <td style={td}>
                  <select
                    value={u.role}
                    onChange={e => handleRoleChange(u.id, e.target.value)}
                    style={{ ...INP, padding:'6px 10px', fontSize:'12px', width:'auto' }}
                  >
                    {roles.map(r => <option key={r.id} value={r.name}>{r.display_name}</option>)}
                  </select>
                </td>
                <td style={td}>
                  <span style={{
                    fontSize:'11px', fontWeight:700, padding:'3px 9px', borderRadius:'6px',
                    color: u.is_active ? T.success : T.danger,
                    background: u.is_active ? `${T.success}1a` : `${T.danger}1a`,
                    border: `1px solid ${u.is_active ? T.success : T.danger}44`,
                  }}>
                    {u.is_active ? 'ACTIVE' : 'SUSPENDED'}
                  </span>
                </td>
                <td style={td}>
                  {u.id !== me?.id && (
                    <Btn size="sm" variant={u.is_active ? 'danger' : 'success'} onClick={() => toggleStatus(u)}>
                      {u.is_active ? 'Suspend' : 'Reactivate'}
                    </Btn>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {showAdd && <AddStaffModal roles={roles} onClose={() => setShowAdd(false)} onCreated={load} />}
    </div>
  );
}

function AddStaffModal({ roles, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', password: '', role: roles[0]?.name || '' });
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setMsg(null);
    if (!form.name.trim() || !form.email.trim() || !form.password || !form.role) {
      return setMsg({ type: 'error', text: 'All fields are required.' });
    }
    if (form.password.length < 8) {
      return setMsg({ type: 'error', text: 'Password must be at least 8 characters.' });
    }
    setLoading(true);
    try {
      await createUser(form);
      onCreated();
      onClose();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to add staff member.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Add staff member" onClose={onClose}>
      <label style={LBL}>Full name</label>
      <input style={{ ...INP, marginBottom:'12px' }} value={form.name} onChange={e => setForm(f=>({...f, name:e.target.value}))} />

      <label style={LBL}>Email</label>
      <input style={{ ...INP, marginBottom:'12px' }} value={form.email} onChange={e => setForm(f=>({...f, email:e.target.value}))} />

      <label style={LBL}>Temporary password</label>
      <input style={{ ...INP, marginBottom:'12px' }} type="password" value={form.password} onChange={e => setForm(f=>({...f, password:e.target.value}))} placeholder="At least 8 characters" />

      <label style={LBL}>Role</label>
      <select style={{ ...INP, marginBottom:'16px' }} value={form.role} onChange={e => setForm(f=>({...f, role:e.target.value}))}>
        {roles.map(r => <option key={r.id} value={r.name}>{r.display_name}</option>)}
      </select>

      {msg && <div style={{ fontSize:'12px', marginBottom:'12px', color: T.danger }}>{msg.text}</div>}

      <Btn onClick={submit} disabled={loading}>{loading ? 'Adding...' : 'Add staff member'}</Btn>
    </Modal>
  );
}

// ───────────────────────────── ROLES TAB ─────────────────────────────
function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([listRoles(), listPermissions()])
      .then(([r, p]) => { setRoles(r.data.data); setPermissions(p.data.data); })
      .catch(() => setMsg({ type: 'error', text: 'Failed to load roles.' }))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.display_name}"? This cannot be undone.`)) return;
    try {
      await deleteRole(role.id);
      load();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to delete role.' });
    }
  };

  if (loading) return <Spinner />;

  return (
    <div>
      {msg && <div style={{ fontSize:'13px', marginBottom:'14px', color: T.danger }}>{msg.text}</div>}

      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:'14px' }}>
        <Btn onClick={() => setShowCreate(true)}>+ New role</Btn>
      </div>

      {roles.map(role => (
        <Card key={role.id}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' }}>
            <div>
              <div style={{ fontSize:'15px', fontWeight:800 }}>
                {role.display_name}
                {role.is_system && <span style={{ fontSize:'10px', color:T.muted, fontWeight:600, marginLeft:'8px', border:`1px solid ${T.border}`, borderRadius:'5px', padding:'2px 6px' }}>BUILT-IN</span>}
              </div>
              <div style={{ fontSize:'12px', color:T.muted, marginTop:'2px' }}>
                {role.name} · {role.member_count} staff member{role.member_count !== 1 ? 's' : ''}
              </div>
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <Btn size="sm" variant="outline" onClick={() => setEditingRole(role)}>Edit permissions</Btn>
              {!role.is_system && (
                <Btn size="sm" variant="danger" onClick={() => handleDelete(role)}>Delete</Btn>
              )}
            </div>
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
            {role.permissions.length ? role.permissions.map(p => (
              <span key={p} style={{ fontSize:'11px', color:T.gold, background:`${T.gold}14`, border:`1px solid ${T.gold}33`, borderRadius:'5px', padding:'3px 8px' }}>
                {permissions.find(x => x.key === p)?.label || p}
              </span>
            )) : <span style={{ fontSize:'12px', color:T.muted }}>No permissions assigned</span>}
          </div>
        </Card>
      ))}

      {editingRole && (
        <EditRoleModal role={editingRole} permissions={permissions} onClose={() => setEditingRole(null)}
          onSaved={() => { setEditingRole(null); load(); }} />
      )}
      {showCreate && (
        <EditRoleModal permissions={permissions} onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }} />
      )}
    </div>
  );
}

function EditRoleModal({ role, permissions, onClose, onSaved }) {
  const isNew = !role;
  const [name, setName] = useState(role?.name || '');
  const [displayName, setDisplayName] = useState(role?.display_name || '');
  const [selected, setSelected] = useState(new Set(role?.permissions || []));
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = (key) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const submit = async () => {
    setMsg(null);
    if (isNew) {
      if (!name.trim() || !/^[a-z0-9_]+$/.test(name)) {
        return setMsg({ type: 'error', text: 'Role key must be lowercase letters, numbers, and underscores only.' });
      }
      if (!displayName.trim()) {
        return setMsg({ type: 'error', text: 'Display name is required.' });
      }
    }
    setLoading(true);
    try {
      if (isNew) {
        await createRole({ name, display_name: displayName, permissions: [...selected] });
      } else {
        await updateRole(role.id, { display_name: displayName, permissions: [...selected] });
      }
      onSaved();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Failed to save role.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title={isNew ? 'New role' : `Edit "${role.display_name}"`} onClose={onClose}>
      {isNew && (
        <>
          <label style={LBL}>Role key (internal, lowercase, no spaces)</label>
          <input style={{ ...INP, marginBottom:'12px' }} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. warehouse" />
        </>
      )}

      <label style={LBL}>Display name</label>
      <input style={{ ...INP, marginBottom:'16px' }} value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="e.g. Warehouse Staff" />

      <label style={LBL}>Permissions</label>
      <div style={{ display:'flex', flexDirection:'column', gap:'8px', maxHeight:'260px', overflowY:'auto', marginBottom:'16px', border:`1px solid ${T.border}`, borderRadius:'8px', padding:'10px' }}>
        {permissions.map(p => (
          <label key={p.key} style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'13px', cursor:'pointer' }}>
            <input type="checkbox" checked={selected.has(p.key)} onChange={() => toggle(p.key)} />
            {p.label}
          </label>
        ))}
      </div>

      {msg && <div style={{ fontSize:'12px', marginBottom:'12px', color: T.danger }}>{msg.text}</div>}

      <Btn onClick={submit} disabled={loading}>{loading ? 'Saving...' : (isNew ? 'Create role' : 'Save changes')}</Btn>
    </Modal>
  );
}

const th = { textAlign:'left', padding:'10px 14px', fontSize:'11px', color:T.muted, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', borderBottom:`1px solid ${T.border}` };
const td = { padding:'11px 14px', fontSize:'13px', borderBottom:`1px solid ${T.surface}` };
